"use client";

import { OptionType } from "@ea/shared_types/next/ea.types";
import { EA_UTILS } from "@ea/shared_types/next/ea.utils";
import { CancelRounded } from "@mui/icons-material";
import { ReactNode, useCallback, useContext } from "react";
import { Controller, FieldError, useFormContext } from "react-hook-form";
import { useTranslations } from "../../translations/useTranslations";
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteValue,
  Chip,
  MenuItem,
  TextField,
  TextFieldProps,
} from "../materialUI";
import { OptionWithChip } from "../OptionWithChip";
import { Status, StatusKeys } from "../Status";
import { EditableContext } from "./utils";

interface FormAutocompleteProps<
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends Omit<
    AutocompleteProps<OptionType, Multiple, DisableClearable, FreeSolo>,
    "renderInput" | "name"
  > {
  name: string;
  label?: string | null;
  dataTestid: string;
  options: OptionType[];
  placeholder?: string;
  textFieldProps?: TextFieldProps;
  required?: boolean;
  renderOptions?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: OptionType,
  ) => React.ReactNode;
  showHelperText?: boolean;
  helperText?: ReactNode;
  clearableName?: string;
  statusType?: string;
  chipVariant?: "filled" | "outlined";
  additionalOnChange?: (
    option?: AutocompleteValue<OptionType, Multiple, DisableClearable, FreeSolo>,
  ) => void;
}

export const FormAutocomplete = <
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>({
  name,
  dataTestid,
  options,
  label,
  placeholder,
  defaultValue,
  freeSolo,
  textFieldProps,
  required,
  renderOptions,
  showHelperText = true,
  helperText,
  onChange,
  clearableName,
  additionalOnChange,
  disabled,
  statusType,
  chipVariant,
  ...props
}: FormAutocompleteProps<Multiple, DisableClearable, FreeSolo>) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslations("Scripts");
  const editable = useContext(EditableContext);
  const containsDefaultOption = options.some((o) => o.isDefault);

  const optionRenderer = (props, option: OptionType) => {
    const { key, ...restProps } = props;

    if (renderOptions) {
      return renderOptions(props, option);
    }

    if (statusType) {
      return (
        <MenuItem key={key} {...restProps}>
          <Status status={`${statusType}.${option.value}` as StatusKeys} />
        </MenuItem>
      );
    }

    if (containsDefaultOption) {
      return (
        <OptionWithChip
          option={option}
          chipLabel={t("drawer.default")}
          {...restProps}
          key={`${option.value}`}
        />
      );
    }

    return (
      <li
        key={`${key}${option.value}`}
        {...restProps}
        onClick={(e) => {
          e.stopPropagation();
          props.onClick(e);
        }}
      >
        {option.label}
      </li>
    );
  };

  const getRequiredValue = (value: unknown) => {
    if (!required) return false;

    if (required && props.multiple) {
      return Array.isArray(value) && value.length > 0 ? false : true;
    }

    return required;
  };

  const renderStartAdornment = useCallback(
    (value: OptionType | OptionType[] | null) => {
      const hasValue = value && (Array.isArray(value) ? value.length > 0 : !!value.value);

      if (!hasValue) return undefined;

      const handleDelete = (itemValue: string | number | boolean) => {
        if (!Array.isArray(value)) return;

        const newValue = value.filter((item) => item.value !== itemValue);
        setValue(
          name,
          newValue.map((item) => item.value),
        );
      };

      if (Array.isArray(value)) {
        return value.map((v) =>
          statusType ? (
            <Status
              key={`${v.value}`}
              status={`${statusType}.${v.value}` as StatusKeys}
              sx={{
                m: 0.3,
                ...(editable ? {} : { opacity: 0.6 }),
              }}
              onDelete={() => {
                const newValue = value.filter((item) => item.value !== v.value);
                setValue(
                  name,
                  newValue.map((item) => item.value),
                );
              }}
              deleteIcon={editable ? <CancelRounded style={{ color: "white" }} /> : <></>}
            />
          ) : (
            <Chip
              key={`${v.value}`}
              label={v.label}
              sx={{
                m: 0.3,
                ...(editable ? {} : { opacity: 0.6 }),
              }}
              variant={chipVariant}
              onDelete={() => handleDelete(v.value)}
              deleteIcon={editable ? <CancelRounded /> : <></>}
            />
          ),
        );
      }
      if (!Array.isArray(value) && statusType) {
        return (
          <Status
            status={`${statusType}.${value.value}` as StatusKeys}
            sx={{
              m: 0.3,
              ...(editable ? {} : { opacity: 0.6 }),
            }}
          />
        );
      }

      return undefined;
    },
    [editable, chipVariant, name, setValue, statusType],
  );

  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      control={control}
      render={({ field }) => {
        const value: typeof field.value | null = props.multiple
          ? Array.isArray(field.value)
            ? options.filter((option) => field.value.includes(option.value))
            : []
          : options.find((option) => option.value === field.value) || null;

        return (
          <Autocomplete
            {...field}
            options={options}
            data-testid={dataTestid}
            getOptionLabel={(option) => option.label}
            value={value}
            fullWidth
            freeSolo={freeSolo}
            readOnly={disabled || !editable}
            disabled={disabled || !editable}
            classes={{
              root: editable ? "" : "MuiReadOnly",
            }}
            renderOption={optionRenderer}
            getOptionKey={(option: OptionType) => `${option.value}`}
            onChange={
              onChange ||
              ((_, selectedOption) => {
                if (!selectedOption && clearableName) {
                  setValue(clearableName, null);
                  return;
                }
                if (props.multiple && Array.isArray(selectedOption)) {
                  field.onChange(selectedOption.map((option) => option.value));
                } else if (!props.multiple && selectedOption) {
                  field.onChange(selectedOption.value);
                } else {
                  field.onChange(selectedOption);
                }
                additionalOnChange?.(selectedOption);
              })
            }
            isOptionEqualToValue={(option, value) => {
              return option.value === value.value;
            }}
            renderInput={(params) => {
              const fieldError = EA_UTILS.object.getNestedValue(errors, name) as
                | FieldError
                | undefined;

              const finalHelperText =
                showHelperText && helperText
                  ? helperText
                  : showHelperText && fieldError
                    ? fieldError.message
                    : "";

              return (
                <TextField
                  {...params}
                  size="small"
                  error={!!fieldError}
                  label={label}
                  focused={false}
                  helperText={finalHelperText}
                  required={getRequiredValue(value)}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: renderStartAdornment(value),
                  }}
                  inputProps={{
                    ...params.inputProps,
                    style: statusType ? { visibility: "hidden" } : {},
                  }}
                  {...textFieldProps}
                />
              );
            }}
            {...props}
          />
        );
      }}
    />
  );
};
